/**
 * Identidade desta sessão da aplicação — um documento carregado do msteams-app.
 *
 * O `sessionId` é gerado uma vez por carga da página: sobrevive à suspensão do app pelo Teams
 * (o documento continua vivo, só oculto) e morre no reload/unload. É o que distingue, no
 * backend e no log remoto, as várias instâncias que um mesmo usuário pode ter abertas ao
 * mesmo tempo (abas do Teams web, pop-out, Teams desktop, Android, iOS). Vai em toda
 * requisição autenticada; o backend só o cria no check-in e o renova nas demais.
 *
 * `clientType`/`hostName` são rótulos para o console de administração, lidos do contexto do
 * Teams quando ele fica disponível (`setHostInfo`). Não entram no payload memoizado do check-in
 * de propósito: mudar aquele payload dispara um novo check-in, que derruba o softphone.
 *
 * `teamsAppId` (`context.app.appId`: o id que o catálogo do Teams gerou ao instalar o
 * app — não o `id` do manifest) e `entityId` (a aba estática) também vêm do contexto via
 * `setHostInfo`, e `querystring` é a query string do `contentUrl` (ex.: `?brand=…` de
 * um manifest white-label), capturada na avaliação deste módulo — antes de o onboarding reescrever
 * a URL com `history.replaceState`. Os três vão só no corpo do check-in, também fora do payload
 * memoizado.
 *
 * `appVersion` é a versão do bundle, injetada pelos scripts `start`/`build` do package.json
 * (`REACT_APP_VERSION=$npm_package_version`). Lida de `process.env` direto, não de
 * `getRuntimeEnv`: é propriedade do bundle, não configuração de ambiente, e o `env.js` gerado
 * pelo server.js não deve poder sobrescrevê-la.
 */

export const sessionId: string = (() => {
  try {
    return crypto.randomUUID().slice(0, 8);
  } catch {
    return Math.random().toString(16).slice(2, 10);
  }
})();

export const appVersion: string = process.env.REACT_APP_VERSION || "";

export const querystring: Record<string, string> = (() => {
  try {
    return Object.fromEntries(new URLSearchParams(window.location.search));
  } catch {
    return {};
  }
})();

let clientType = "";
let hostName = "";
let teamsAppId = "";
let entityId = "";

export function setHostInfo(info: { clientType?: string; hostName?: string; teamsAppId?: string; entityId?: string }): void {
  clientType = info.clientType || "";
  hostName = info.hostName || "";
  teamsAppId = info.teamsAppId || "";
  entityId = info.entityId || "";
}

export type SessionIdentity = {
  sessionId: string;
  clientType: string;
  hostName: string;
  appVersion: string;
};

export function getSessionIdentity(): SessionIdentity {
  return { sessionId, clientType, hostName, appVersion };
}

export function getTeamsAppId(): string {
  return teamsAppId;
}

export function getEntityId(): string {
  return entityId;
}
